/**
 * Content hubs for the workflow how-to series.
 *
 * One hub per audience. Each hub frames that audience's problem, summarizes
 * every workflow written for it, and links to the detailed article plus the
 * product pages the workflow touches. The article set is fixed and finite (31
 * posts covering the playbooks in `docs/workflow-playbooks.md`), so the mapping
 * lives here as data rather than being derived from the posts collection;
 * ordering is editorial, not chronological.
 *
 * Most articles sit in exactly one hub. The category batch (W29 to W31) is the
 * exception: those guides serve more than one audience, so the same slug is
 * carded in several hubs and the series count dedupes by slug.
 *
 * These pages are English only, matching the series itself. Register any new
 * hub in `marketing-pages.ts` so it reaches the sitemap.
 */

import { AUTHORS, type Author } from "@marketing/shared/lib/authors";
import { getMarketingPlanPrices } from "@marketing/shared/lib/plan-prices";

export interface HubWorkflow {
	/** Playbook id from docs/workflow-playbooks.md, shown as a small label. */
	id: string;
	title: string;
	/** Post slug under apps/web/content/posts. */
	slug: string;
	summary: string;
}

export interface HubGroup {
	title: string;
	lede: string;
	workflows: HubWorkflow[];
}

export interface HubProductLink {
	label: string;
	href: string;
	description: string;
}

export interface HubFaqItem {
	question: string;
	answer: string;
}

export interface WorkflowHub {
	/** Locale-less path, matching the entry in marketing-pages.ts. */
	path: string;
	/** Short audience label used in the hub cross-links. */
	audience: string;
	eyebrow: string;
	/** The h1. */
	title: string;
	/** Short name for breadcrumbs, structured-data names, and cross-links. */
	shortTitle: string;
	metaTitle: string;
	metaDescription: string;
	ogCardTitle: string;
	keywords: string[];
	/** Byline; the same record feeds the schema.org Person. */
	author: Author;
	/** ISO date the hub first shipped. Feeds datePublished; does not move. */
	published: string;
	/** ISO date of the last editorial pass. Shown in the byline; feeds dateModified. */
	updated: string;
	lede: string;
	problem: {
		title: string;
		paragraphs: string[];
		takeaways: string[];
	};
	groups: HubGroup[];
	products: HubProductLink[];
	faq: HubFaqItem[];
	cta: {
		title: string;
		description: string;
		primary: string;
		primaryHref: string;
		secondary: string;
		secondaryHref: string;
	};
}

const agencies: WorkflowHub = {
	path: "/guides/ai-visibility-for-marketing-agencies",
	audience: "Marketing agencies",
	eyebrow: "For marketing agencies",
	title: "AI visibility for marketing agencies: eight workflows for running an AI search practice",
	shortTitle: "AI visibility guides for marketing agencies",
	metaTitle: "AI Visibility Guides for Marketing Agencies | Spyglasses",
	metaDescription:
		"Eight workflows for running an AI search practice: map a category, prospect with visibility reports, baseline a new client, track over time, report monthly, displace competitors, and measure a new market.",
	ogCardTitle: "AI Visibility Guides for Marketing Agencies",
	keywords: [
		"AI visibility for agencies",
		"AEO agency workflow",
		"GEO agency services",
		"AI search practice",
		"AI visibility guides for agencies",
		"AI search practice workflows",
		"AI share of voice reporting",
		"client AI visibility reporting",
	],
	author: AUTHORS.jimWrubel,
	published: "2026-09-10",
	updated: "2026-09-18",
	lede: "Clients are asking what ChatGPT, Gemini, and Google AI Overviews say about them. These eight workflows turn that question into a practice you can sell, deliver, and renew, from the first visibility report through monthly reporting and competitive displacement.",
	problem: {
		title: "Your clients are asking about AI. The answer can't be a screenshot.",
		paragraphs: [
			"Clients started asking what ChatGPT says about them, and a lot of agencies answered with a screenshot. That works once. It doesn't work as a retainer, because the same prompt gives a different answer tomorrow and the client notices.",
			"The way out is to stop treating the final answer as the thing you optimize. Answers vary by session. The steps before the answer don't. AI searches the web, ranks what it finds, reads pages, and scores them for relevance, and those steps follow published rules you can work against. Track the answers to prove the work; do the work on the pipeline.",
			"Each workflow below is a full article with the steps, the order, and what to hand the client at the end. Read them in sequence if you're building the practice from scratch, or jump to the one you need this week.",
		],
		takeaways: [
			"AI answers vary by session, so a single screenshot isn't a measurement.",
			"The retrieval steps behind the answer are repeatable, and that's where the work happens.",
			"An AI practice is a sequence: prospect, baseline, track, report, then displace.",
			"Client-ready exports matter as much as the data; the deliverable is what gets renewed.",
		],
	},
	groups: [
		{
			title: "Win the work",
			lede: "The pitch is easier when the prospect's own numbers are on the screen, and easier still when you mapped their market first.",
			workflows: [
				{
					id: "W4",
					title: "How to Prospect New Clients with AI Visibility Reports",
					slug: "how-to-prospect-new-clients-with-ai-visibility-reports",
					summary:
						"Run a visibility report on a prospect before you ever get on a call, and open with what AI already says about them. A free preview is usually enough to start the conversation, and the paid report becomes the first deliverable. It turns cold outreach into a discussion about their own numbers.",
				},
				{
					id: "W29",
					title: "How to Map a Category in AI Answers",
					slug: "how-to-map-a-category-in-ai-answers",
					summary:
						"Track a vertical before anyone in it has signed. Name the market, generate the questions buyers ask about it, and the first report hands back every brand AI names when those questions get asked. One property covers a whole practice area instead of one per lead.",
				},
				{
					id: "W30",
					title: "How to Turn a Category Map Into Prospects",
					slug: "how-to-turn-a-category-map-into-prospects",
					summary:
						"A market ranking is also a list of who AI barely names. Match each brand's recognition against how often it gets named, shortlist the ones with a visible gap, then pitch with a branded report about their own category. The list refreshes itself every week.",
				},
			],
		},
		{
			title: "Set the account up",
			lede: "Two workflows, run once per client, that everything else depends on.",
			workflows: [
				{
					id: "W1",
					title: "How to Baseline Your Brand's AI Visibility",
					slug: "how-to-baseline-your-brands-ai-visibility",
					summary:
						"Every engagement starts with a number you can defend. Run the first report, correct the brand snapshot behind it, then read share of voice, consistency, and the competitor set together instead of one at a time. That baseline is what every later month gets measured against.",
				},
				{
					id: "W2",
					title: "How to Track Your Brand's AI Visibility Over Time",
					slug: "how-to-track-ai-visibility-over-time",
					summary:
						"One report is a snapshot; tracking is the product. Expand the prompt set, prune the auto-seeded competitors down to the ones you actually care about, then put the whole thing on a daily schedule with a project and a goal attached. After that the trend line builds itself.",
				},
			],
		},
		{
			title: "Run the retainer",
			lede: "The recurring work, plus the two engagements clients ask for by name.",
			workflows: [
				{
					id: "W5",
					title: "How to Build Monthly AI Visibility Client Reports",
					slug: "how-to-build-monthly-ai-visibility-client-reports",
					summary:
						"Log the work you did as annotations so the chart explains itself, then let the scheduled report land in the client's inbox on the same day each month. Export the white-labeled PDF or deck for the meeting. The story is the work sitting next to the movement, not a wall of metrics.",
				},
				{
					id: "W6",
					title: "How to Displace a Competitor in AI Answers",
					slug: "how-to-displace-a-competitor-in-ai-answers",
					summary:
						"Pick the competitor who keeps showing up instead of your client, then find the exact queries and URLs holding those spots. From there it's either content that beats the page or a pitch to the publisher AI keeps citing. Measure the swap with a project so the win is something you can show.",
				},
				{
					id: "W7",
					title: "How to Measure AI Search Visibility in a New Market",
					slug: "how-to-measure-ai-visibility-in-a-new-market",
					summary:
						"AI answers differ by city and country, so a new market needs its own measurement rather than a footnote on the home one. Add the market as a location, localize the competitor list, and check that the inherited prompts still make sense there. Then compare markets side by side.",
				},
			],
		},
	],
	products: [
		{
			label: "AI Visibility Report",
			href: "/ai-visibility-report",
			description:
				"The prospecting hook and the first deliverable. Free preview, $15 for the full report.",
		},
		{
			label: "Daily Prompt Tracking",
			href: "/prompt-tracking",
			description:
				"Prompts run on a schedule across the major assistants, with shareable client-facing KPIs.",
		},
		{
			label: "For SEO and marketing agencies",
			href: "/for-agencies",
			description:
				"White-labeled exports, multiple properties, and per-client usage billing.",
		},
		{
			label: "Pricing",
			href: "/pricing",
			description:
				"What an agency plan covers, including tracked prompts and audit pages.",
		},
	],
	faq: [
		{
			question: "How is this different from a prompt tracker?",
			answer: "Prompt trackers report where you stand, which is useful and is table stakes at this point. The gap is knowing what to do next. Every workflow here ends in a change you can make and a way to check whether it worked.",
		},
		{
			question: "What do I show a client in month one?",
			answer: "A baseline report with share of voice, brand consistency, the competitor set, and a per-prompt breakdown, plus the prompt set you'll track from then on. Start the tracking in the same month so month two has something to compare against.",
		},
		{
			question:
				"Where do I start with a client who has never measured AI visibility?",
			answer: "Run the baseline workflow first. It gives you one defensible number, a corrected brand snapshot, and a competitor set, and everything you report later gets measured against it.",
		},
		{
			question: "How do I show a client that the work moved the number?",
			answer: "Log each piece of work as an annotation on the trend line, then let the monthly report put the work next to the movement. The story is the sequence, not the metric on its own.",
		},
	],
	cta: {
		title: "Start with a prospect's numbers",
		description:
			"Run a free preview on any domain and see what the major assistants say about it today. It takes a few minutes and it's the same report you'd hand a client.",
		primary: "Run a visibility report",
		primaryHref: "/ai-visibility-report",
		secondary: "See agency plans",
		secondaryHref: "/for-agencies",
	},
};

const pr: WorkflowHub = {
	path: "/guides/ai-visibility-for-pr-teams",
	audience: "PR and communications",
	eyebrow: "For PR and communications teams",
	title: "AI visibility for PR teams: twelve workflows for earned media in AI answers",
	shortTitle: "AI visibility guides for PR and communications teams",
	metaTitle:
		"AI Visibility Guides for PR and Communications Teams | Spyglasses",
	metaDescription:
		"Twelve workflows for earned media in AI answers: map a category before you have the client, score outlets before you pitch, connect placements to citations, run crisis comms, track rebrands and campaigns, and fix the domains AI gets wrong.",
	ogCardTitle: "AI Visibility Guides for PR and Comms Teams",
	keywords: [
		"PR AI visibility guides",
		"earned media AI workflows",
		"AI placement value",
		"crisis communications AI",
		"press release AI citation",
		"media monitoring AI answers",
		"publisher AI accessibility",
		"executive visibility AI",
	],
	author: AUTHORS.jimWrubel,
	published: "2026-09-10",
	updated: "2026-09-18",
	lede: "Coverage is only half the story now. These twelve workflows show PR and communications teams whether ChatGPT, Gemini, Claude, and Perplexity can see a placement, whether they're citing it, and what that earned media is worth in AI answers.",
	problem: {
		title: "A great placement AI can't read is worth less than you think.",
		paragraphs: [
			"Coverage reports have a new blind spot. Plenty of major publications block AI crawlers such as GPTBot, ClaudeBot, and PerplexityBot in robots.txt, so a placement that looks excellent on a clip report can have close to zero influence on what ChatGPT, Gemini, Claude, or Perplexity says about your client.",
			"Three measurements close that gap. An AI Placement Value Score says whether an outlet is worth pitching at all, by combining its organic authority with whether AI can actually read it. A coverage group's citation rate says which of your published placements assistants actually quote, cited against not cited. Citation Intelligence says which sources are shaping the answers, including earned media you never pitched. Score the outlet before you pitch, score the placement after it publishes, then give the coverage group a few weeks to show up.",
			"The workflows below cover the whole arc, from building a pitch list through campaign wrap-up and the weeks when the story turns against you. Each one links to a full article with the steps in order.",
		],
		takeaways: [
			"Many top outlets block AI crawlers in robots.txt, so media tier and AI value are not the same thing.",
			"Score the outlet with an AI Placement Value Score before the pitch, and the placement itself after it publishes.",
			"Assistants cite sources you never pitched, and some of that hidden earned media is worth chasing.",
			"Executives, campaigns, and crises each need their own tracking; brand-level numbers hide all three.",
		],
	},
	groups: [
		{
			title: "Start from the market",
			lede: "Three workflows that begin with a category instead of a client.",
			workflows: [
				{
					id: "W29",
					title: "How to Map a Category in AI Answers",
					slug: "how-to-map-a-category-in-ai-answers",
					summary:
						"Some markets need measuring before anyone in them is a client. Name the market, write the questions buyers ask about it, and the first report comes back with every brand AI names. From there it's a ranking that moves every day, with a 7-day change on each name.",
				},
				{
					id: "W30",
					title: "How to Turn a Category Map Into Prospects",
					slug: "how-to-turn-a-category-map-into-prospects",
					summary:
						"A brand buyers know well that AI rarely names has a gap it can be shown, and that gap is the pitch. Read the ranking against recognition, shortlist the brands worth calling, and send a branded report about their own market before you ask for the meeting.",
				},
				{
					id: "W31",
					title: "How to Watch the Narrative in a Category",
					slug: "how-to-watch-the-narrative-in-a-category",
					summary:
						"A ranking answers who; a narrative answers how. Write down the claims the market is supposed to stand for, cover the awareness questions that pull them out, then follow the citations back to the handful of publishers the story is actually coming from.",
				},
			],
		},
		{
			title: "Before the pitch",
			lede: "Two checks that take minutes and change what you send.",
			workflows: [
				{
					id: "W9",
					title: "How to Build a PR Pitch List AI Can Actually See",
					slug: "how-to-build-a-pr-pitch-list-ai-can-actually-see",
					summary:
						"Before you pitch an outlet, check whether AI can read it at all. Score the publisher for AI value, then score the specific placement idea before you negotiate. A smaller outlet that assistants actually read can beat a big one they can't.",
				},
				{
					id: "W13",
					title: "How to Optimize a Press Release for AI Citation Before It Goes Out",
					slug: "how-to-optimize-a-press-release-for-ai-citation",
					summary:
						"Paste the draft release into the citation pipeline and score it against the queries you want it to win, gate by gate. Fix whatever falls out, then score it again. It takes about twenty minutes and it happens before distribution, not after.",
				},
			],
		},
		{
			title: "After the placement",
			lede: "Turning clips into a number the client or the board will accept.",
			workflows: [
				{
					id: "W8",
					title: "How to Connect Earned Media to AI Visibility",
					slug: "how-to-connect-earned-media-to-ai-visibility",
					summary:
						"Group a campaign's placements, score them, then watch which ones AI actually cites. The gap between cited and not cited is the most useful line in the report. It's also the fastest way to show that coverage volume and coverage influence are different things.",
				},
				{
					id: "W27",
					title: "How to Track Campaign Visibility in AI Answers",
					slug: "how-to-track-campaign-visibility-in-ai-answers",
					summary:
						"Load the campaign's messages and target outlets before launch so day one has a baseline. Tag the campaign prompts, annotate the flight dates, then watch message pull-through as placements land. The retrospective mostly writes itself at the end.",
				},
			],
		},
		{
			title: "When the story turns",
			lede: "The two workflows you want configured before you need them.",
			workflows: [
				{
					id: "W12",
					title: "How to Run Crisis Communications When AI Is Repeating the Story",
					slug: "how-to-run-crisis-communications-when-ai-is-repeating-the-story",
					summary:
						"When a bad story breaks, the question isn't only what reporters are writing. It's whether assistants have started repeating it. Capture the narrative and your counter-message, run reputational prompts nightly, and trace which sources feed the answers so you know where to aim corrections.",
				},
				{
					id: "W28",
					title: "How to See Which Sites AI Trusts (and Fix the Ones It Gets Wrong)",
					slug: "how-to-see-which-sites-ai-trusts-and-fix-the-ones-it-gets-wrong",
					summary:
						"Sometimes a model searches one specific site before it answers anything. That list is AI's trusted-source list, and it works as both a pitch target list and a warning system. A look-alike domain on it means your authority is landing somewhere you don't own.",
				},
			],
		},
		{
			title: "Long-running narratives",
			lede: "Name changes, deals, and the people who speak for the brand.",
			workflows: [
				{
					id: "W10",
					title: "How to Track a Rebrand in AI Answers",
					slug: "how-to-track-a-rebrand-in-ai-answers",
					summary:
						"AI keeps using the old name long after the press release. Keep the old name as an alias, load the new positioning as key messages, annotate launch day, then watch the handover in consistency and message pull-through. After that it's a hunt for the pages still carrying stale branding.",
				},
				{
					id: "W11",
					title: "How to Track AI Visibility Through a Merger",
					slug: "how-to-track-ai-visibility-through-a-merger",
					summary:
						"Track the acquirer as the property and the acquired brand as a competitor while the deal is open, then flip it to an alias after the migration. Watch share of voice move between the two names. Annotate the announcement and the close so every break in the trend has a reason attached.",
				},
				{
					id: "W14",
					title: "How to Build Executive Visibility in AI Answers",
					slug: "how-to-build-executive-visibility-in-ai-answers",
					summary:
						"Executives get asked about by name, and assistants often get the answer wrong or blend two people who share a name. Track the person as their own subject, compare them with peers instead of competitors, and run person prompts nightly. Identity accuracy is the number to watch.",
				},
			],
		},
	],
	products: [
		{
			label: "AI Placement Value Score",
			href: "/ai-placement-value-score",
			description:
				"What an outlet is worth in AI discovery, combining organic authority with whether AI can read it.",
		},
		{
			label: "Citation Intelligence",
			href: "/citation-intelligence",
			description:
				"Every source behind an answer, classified by brand relevance, including earned media you never pitched.",
		},
		{
			label: "Person Tracking",
			href: "/person-tracking",
			description:
				"Daily monitoring of what assistants say about a named executive or spokesperson.",
		},
		{
			label: "For PR and comms",
			href: "/for-pr-firms",
			description:
				"Coverage groups, placement scoring, and board-ready exports in one place.",
		},
	],
	faq: [
		{
			question:
				"How do I know if an outlet is worth pitching for AI visibility?",
			answer: "Score it. A publisher score combines how much authority the site has with whether AI can actually read it, so an outlet that blocks crawlers scores lower than its reputation suggests.",
		},
		{
			question: "How long after a placement should I expect a citation?",
			answer: "It depends on how often AI refetches that site, which ranges from a few days to a lot longer. Watch the coverage group rather than checking one prompt by hand, and give it a few weeks before you draw conclusions.",
		},
		{
			question: "Can I track a person instead of a brand?",
			answer: "Yes. An executive or spokesperson can be tracked as their own subject, with peers in place of competitors and identity accuracy in place of brand consistency.",
		},
		{
			question: "What do I do about a negative story AI keeps repeating?",
			answer: "Record the claim so you get told when it surfaces, run reputational prompts nightly, then trace which sources feed the answers. Prioritize corrections by how much AI reads each source rather than by the size of the outlet.",
		},
	],
	cta: {
		title: "Find out what AI says about your client today",
		description:
			"A visibility report covers ChatGPT, Gemini, Claude, and Perplexity, with the sources behind each answer. Free preview, and the full report is shareable and white-labeled.",
		primary: "Run a visibility report",
		primaryHref: "/ai-visibility-report",
		secondary: "Score a publisher",
		secondaryHref: "/ai-placement-value-score",
	},
};

const inHouse: WorkflowHub = {
	path: "/guides/ai-visibility-for-in-house-teams",
	audience: "In-house marketing",
	eyebrow: "For in-house marketing teams",
	title: "AI visibility for in-house marketing teams: nine workflows for treating AI as a channel",
	shortTitle: "AI visibility guides for in-house marketing teams",
	metaTitle: "AI Visibility Guides for In-House Marketing Teams | Spyglasses",
	metaDescription:
		"Nine workflows for treating AI as a channel: measure crawls and clickthroughs, fix citation blockers, govern crawlers, time updates to recrawl cadence, launch products, monitor what AI says, and map the market around you.",
	ogCardTitle: "AI Visibility Guides for In-House Marketing Teams",
	keywords: [
		"AI as a marketing channel",
		"AI traffic analytics",
		"AI crawler governance",
		"AI citation blockers",
		"AI recrawl cadence",
		"brand safety AI monitoring",
		"product launch AI visibility",
		"AI bot traffic",
	],
	author: AUTHORS.jimWrubel,
	published: "2026-09-10",
	updated: "2026-09-18",
	lede: "AI assistants are sending you visitors and their crawlers are reading your site every day. These nine workflows give an in-house marketing team a way to measure AI as a channel, govern the crawlers, and report on what it says about the brand.",
	problem: {
		title: "AI became a channel before anyone built the channel report.",
		paragraphs: [
			"Assistants are sending real people to your site, and they're reading it constantly whether or not you asked them to. Most teams can see neither clearly. Referral data is thin because plenty of assistant visits arrive with no referrer, and the crawling shows up as noise in server logs.",
			"You can close that gap without hiring anyone. Measure what AI takes and what it sends, fix the technical reasons good pages never get cited, decide which bots you actually want, and connect the content you ship to what changes afterward. Put those together and you have a channel report.",
			"Each workflow below links to a full article with the steps and the dashboards they map to. The first two are the ones to run first.",
		],
		takeaways: [
			"AI traffic splits into crawls and human clickthroughs, and the two mean different things.",
			"Plenty of pages fail on technical readiness before the writing ever matters.",
			"Blocking every bot also blocks the assistants that send you visitors.",
			"Tie the work to a goal and a date so any change in the trend has an explanation.",
		],
	},
	groups: [
		{
			title: "Measure the channel",
			lede: "Ground truth first, then connect it to the work you ship.",
			workflows: [
				{
					id: "W17",
					title: "How to Measure AI as a Marketing Channel",
					slug: "how-to-measure-ai-as-a-marketing-channel",
					summary:
						"Install the tracker and you stop guessing. You'll see assistant crawls and real human clickthroughs from AI as separate numbers, forwarded into GA4 next to every other channel. That's the version a CFO will accept.",
				},
				{
					id: "W15",
					title: "How to Connect Content Work to AI Visibility",
					slug: "how-to-connect-content-work-to-ai-visibility",
					summary:
						"Import your pages, group them by the job they do, then attach that group to a project as a goal. Annotate releases so the chart knows when you shipped. After a few weeks you can point at a specific piece of work and show what it moved.",
				},
			],
		},
		{
			title: "Fix what blocks you",
			lede: "The reasons good pages don't get cited are often not about the writing.",
			workflows: [
				{
					id: "W16",
					title: "How to Find and Fix AI Citation Blockers",
					slug: "how-to-find-and-fix-ai-citation-blockers",
					summary:
						"Some pages never get cited for reasons that have nothing to do with the copy. Audit the site across the dimensions AI actually cares about, fix the blockers, then re-audit to confirm. Start with the pages that already matter to the business.",
				},
				{
					id: "W21",
					title: "How to Time Content Updates to AI Recrawl Cadence",
					slug: "how-to-time-content-updates-to-ai-recrawl-cadence",
					summary:
						"AI refetches some pages weekly and others almost never. Find your real cadence page by page, then time updates and announcements to it instead of to your publishing calendar. The starved pages are the ones to fix first.",
				},
			],
		},
		{
			title: "Govern the crawl",
			lede: "See who's taking what, then decide what you allow.",
			workflows: [
				{
					id: "W20",
					title: "How to See Which Bots Are Crawling Your Site",
					slug: "how-to-see-which-bots-are-crawling-your-site",
					summary:
						"Assistants, model trainers, search crawlers, and scrapers all look like traffic until you split them apart. Watch the categories over time, drill into a single bot, and slice by page to see what each one takes. Reporting first; decisions come after.",
				},
				{
					id: "W18",
					title: "How to Govern AI Crawlers on Your Site Without Losing AI Visibility",
					slug: "how-to-govern-ai-crawlers-on-your-site",
					summary:
						"Blocking every bot costs you the assistants that send real visitors. Separate the crawlers that train models from the ones that answer live questions, allow what you want to be visible in, then watch the 403s to confirm the rules do what you meant.",
				},
			],
		},
		{
			title: "Launch and protect",
			lede: "Two moments where being set up early is most of the value.",
			workflows: [
				{
					id: "W19",
					title: "How to Launch a Product With AI Visibility From Day One",
					slug: "how-to-launch-a-product-with-ai-visibility-from-day-one",
					summary:
						"Set the product up as its own subject before launch, even though AI has nothing to say about it yet. Those zeros are the baseline that makes the launch curve readable. Fill in the snapshot, generate comparison prompts, and start nightly runs on day one.",
				},
				{
					id: "W3",
					title: "How to Monitor What AI Says About Your Brand",
					slug: "how-to-monitor-what-ai-says-about-your-brand",
					summary:
						"Write down the claims you don't want repeated, then let nightly runs tell you when one starts surfacing. Alerts reach legal and comms in an inbox instead of waiting on a dashboard nobody opened. When something does surface, trace the sources feeding it.",
				},
			],
		},
		{
			title: "Watch the market",
			lede: "Your own numbers read differently next to the whole field's.",
			workflows: [
				{
					id: "W29",
					title: "How to Map a Category in AI Answers",
					slug: "how-to-map-a-category-in-ai-answers",
					summary:
						"Buyers ask AI who the options are before they ask about you. Track the market itself, with no brand at the center of it, and the ranking shows every name AI volunteers and which ones are gaining. It runs alongside your brand tracking rather than replacing it.",
				},
			],
		},
	],
	products: [
		{
			label: "AI Traffic Analytics",
			href: "/ai-traffic-analytics",
			description:
				"Server-side detection of AI agents, crawlers, and human clickthroughs from AI, in real time.",
		},
		{
			label: "AI Readiness Site Audit",
			href: "/ai-readiness-audit",
			description:
				"Scores every page across seven weighted dimensions and surfaces the technical blockers.",
		},
		{
			label: "Daily Prompt Tracking",
			href: "/prompt-tracking",
			description:
				"Prompts run daily across the major assistants, with the backing metrics and raw citations.",
		},
		{
			label: "Pricing",
			href: "/pricing",
			description: `Plans start at $${getMarketingPlanPrices().brand.monthlyPrice} a month, including tracked prompts and audit pages.`,
		},
	],
	faq: [
		{
			question: "Does AI traffic show up in Google Analytics?",
			answer: "Partly, and not reliably. Some assistant referrals arrive with no referrer at all, and crawler visits aren't sessions in the first place. Server-side detection separates the two and can forward the clean version into GA4.",
		},
		{
			question: "Should I block AI crawlers?",
			answer: "Not all of them. The bots that train models and the ones that answer live questions are different populations, and blocking everything removes you from the answers that send visitors. Split them and decide per category.",
		},
		{
			question: "Why doesn't AI cite our best pages?",
			answer: "Often it's technical. The content isn't reachable, the page renders too slowly, or the answer is buried somewhere a retrieval step won't find it. A readiness audit catches those before you rewrite anything.",
		},
		{
			question: "How fast does new content show up in AI answers?",
			answer: "It follows how often AI refetches your site, and that varies a lot page by page. Measure your own cadence and time updates to it rather than assuming a week.",
		},
	],
	cta: {
		title: "See what AI is doing on your site",
		description:
			"Start with the numbers you already have. A visibility report shows what assistants say about you, and traffic analytics shows what they take and what they send back.",
		primary: "Run a visibility report",
		primaryHref: "/ai-visibility-report",
		secondary: "See AI Traffic Analytics",
		secondaryHref: "/ai-traffic-analytics",
	},
};

const content: WorkflowHub = {
	path: "/guides/ai-visibility-for-content-teams",
	audience: "Content teams",
	eyebrow: "For content teams",
	title: "AI visibility for content teams: five workflows for getting content cited by AI",
	shortTitle: "AI visibility guides for content teams",
	metaTitle: "AI Visibility Guides for Content Teams | Spyglasses",
	metaDescription:
		"Five workflows for getting content cited by AI: score a draft before publishing, win a target grounding search, optimize pages you already have, fix cannibalizing pages, and find gaps from real questions.",
	ogCardTitle: "AI Visibility Guides for Content Teams",
	keywords: [
		"content optimization for AI",
		"AI citation optimization",
		"grounding search optimization",
		"fan-out queries",
		"pre-publish content check",
		"content cannibalization AI",
		"content gap analysis AI",
		"AEO content workflow",
	],
	author: AUTHORS.jimWrubel,
	published: "2026-09-10",
	updated: "2026-09-10",
	lede: "Getting cited by AI is a different job from ranking in Google. These five workflows cover the editorial steps a content team takes so a page survives AI retrieval and gets cited by ChatGPT and AI Overviews.",
	problem: {
		title: "Getting cited is a different job from ranking.",
		paragraphs: [
			"A search engine ranks your page. An assistant reads a piece of your page and decides whether to use that piece in an answer. Those are different jobs, and a page can do the first one well while losing the second one badly.",
			"The useful part is that the retrieval steps are testable before you publish. You can score a draft against the queries it should win, see the exact gate where it drops out, and fix it while it's still a draft. That's a normal editorial step, not a new discipline.",
			"The workflows below run from a first draft through to finding the next thing worth writing. Each links to a full article with the steps in order.",
		],
		takeaways: [
			"Assistants retrieve passages, so the section is the unit of work, not the page.",
			"You can score a draft against real queries before it ever goes live.",
			"Two pages aimed at one query usually means neither of them gets picked.",
			"Real questions from search and forums make better briefs than keyword lists.",
		],
	},
	groups: [
		{
			title: "Before you publish",
			lede: "Two ways in: start from the draft, or start from the query.",
			workflows: [
				{
					id: "W25",
					title: "How to Check a Draft for AI Citation Before Publishing",
					slug: "how-to-check-a-draft-for-ai-citation-before-publishing",
					summary:
						"Score the draft before it goes live, against the real queries it should win. You get a gate-by-gate read on where it falls out of the pipeline and what to change. Fixing it now costs far less than rewriting a published page later.",
				},
				{
					id: "W23",
					title: "How to Win a Target AI Grounding Search",
					slug: "how-to-win-a-target-ai-grounding-search",
					summary:
						"Start from the query, not the page. Pick a real fan-out query you want to win, let the tool suggest your best-matching page, then score and revise until it survives the pipeline. Publish, then watch the rank for that query.",
				},
			],
		},
		{
			title: "After you publish",
			lede: "Most of the available lift is in pages you already own.",
			workflows: [
				{
					id: "W22",
					title: "How to Optimize Existing Content for AI Citation",
					slug: "how-to-optimize-existing-content-for-ai-citation",
					summary:
						"Your best opportunity is usually a page you already have. Sort by impact to pick the target, run the score and rewrite loop, then measure the change with a project. Most of the gain comes from structure and specifics rather than from adding length.",
				},
				{
					id: "W24",
					title: "How to Fix Pages Competing for the Same Query",
					slug: "how-to-fix-pages-competing-for-the-same-query",
					summary:
						"Two pages aimed at the same query split the signal and neither one wins. Overlap detection flags the pair, then you pick a canonical page and either differentiate the other or fold it in. Score both again to confirm the split is gone.",
				},
			],
		},
		{
			title: "Find the next thing to write",
			lede: "Briefs built from questions people are already asking.",
			workflows: [
				{
					id: "W26",
					title: "How to Find Content Gaps From Real AI Questions",
					slug: "how-to-find-content-gaps-from-real-ai-questions",
					summary:
						"The best briefs come from questions people are already asking. Find the fan-out queries where you don't rank at all, pull real questions from People Also Ask, Reddit, and Quora, then publish against them. Track the result so the next brief starts better informed.",
				},
			],
		},
	],
	products: [
		{
			label: "Citation Optimizer",
			href: "/citation-optimizer",
			description:
				"Scores content against the four stages of AI retrieval, then guides rewrites until it survives.",
		},
		{
			label: "FAQ Generator",
			href: "/faq-generation",
			description:
				"Pulls real questions from People Also Ask, Reddit, and Quora, and exports HTML, JSON-LD, and CSV.",
		},
		{
			label: "AI Readiness Site Audit",
			href: "/ai-readiness-audit",
			description:
				"Catches the technical reasons a well-written page still never gets cited.",
		},
		{
			label: "Daily Prompt Tracking",
			href: "/prompt-tracking",
			description:
				"Measures whether the published change actually moved the queries you targeted.",
		},
	],
	faq: [
		{
			question: "Can I score a draft before it's published?",
			answer: "Yes. Paste the draft, pick the queries it should win, and score it gate by gate. You can revise and re-score as many times as you need before anything goes live.",
		},
		{
			question: "Does making a page longer help it get cited?",
			answer: "Not on its own. Retrieval picks a passage, so a specific, well-structured section beats a longer page with the same answer buried in the middle of it.",
		},
		{
			question: "What is a fan-out query?",
			answer: "It's the web search AI writes for itself after someone asks a question. Your page competes for the fan-out query rather than the prompt the person typed, which is why the two lists rarely look alike.",
		},
		{
			question: "How do I know which page to update first?",
			answer: "Sort by the gap between how much a query matters and how well you currently rank for it. The best targets are usually pages that almost make it, not pages you haven't written yet.",
		},
	],
	cta: {
		title: "Score a page before you touch the copy",
		description:
			"Pick a query you want to win, point the optimizer at the page, and see which stage of retrieval drops it. The first score tells you whether the problem is the writing or something else.",
		primary: "Try the Citation Optimizer",
		primaryHref: "/citation-optimizer",
		secondary: "Run a visibility report",
		secondaryHref: "/ai-visibility-report",
	},
};

export const workflowHubs = {
	agencies,
	pr,
	inHouse,
	content,
} as const;

export const allWorkflowHubs: WorkflowHub[] = [agencies, pr, inHouse, content];

/** The other three hubs, for the cross-link band at the foot of each page. */
export function otherWorkflowHubs(current: WorkflowHub): WorkflowHub[] {
	return allWorkflowHubs.filter((hub) => hub.path !== current.path);
}

/**
 * Total articles in the series, used in the hub intros. Counts distinct slugs,
 * because a guide written for more than one audience is carded in more than one
 * hub and would otherwise inflate the total.
 */
export const WORKFLOW_SERIES_COUNT = new Set(
	allWorkflowHubs.flatMap((hub) =>
		hub.groups.flatMap((group) =>
			group.workflows.map((workflow) => workflow.slug),
		),
	),
).size;
